import React, { useState } from 'react';
import StatsPieChart from '../charts/StatsPieChart';
import StatsBarChart from '../charts/StatsBarChart';
import StatBlock from '../StatBlock';
import { toStatItems, asHectares } from '../statBlockUtils';
import type { YearStats, StatsChartData, RegionStats } from '../statsTypes';
import { resolveRegionStats, canonicalRegionLabel } from '../regionResolver';

const REGIONS = ['NT Total', 'Beaufort Delta', 'Sahtu', 'South Slave', 'North Slave', 'Deh Cho'];

const STATUS_COLORS: Record<string, string> = {
  'Out of Control': '#D32F2F',
  'Being Held': '#F57C00',
  'Under Control': '#FBC02D',
  'Declared Out': '#388E3C',
  'New': '#1565C0',
};

const RESPONSE_COLORS: Record<string, string> = {
  'Full Response': '#1976D2',
  'Modified Response': '#7B1FA2',
  'Monitor': '#00838F',
};

interface HistoricalTabProps {
  data: YearStats[];
}

function getRegionStats(yearData: YearStats, regionName: string): RegionStats | null {
  if (regionName === 'NT Total') return yearData.totals;
  return resolveRegionStats(yearData.regions, regionName);
}

const HistoricalTab: React.FC<HistoricalTabProps> = ({ data }) => {
  const [activeYearIdx, setActiveYearIdx] = useState(0);
  const [activeRegion, setActiveRegion] = useState('NT Total');

  if (!data || data.length === 0) return <p>No historical data available</p>;

  const activeYear = data[activeYearIdx];
  const stats = getRegionStats(activeYear, activeRegion);

  const causeData: StatsChartData[] = stats
    ? [
        { name: 'Natural', value: stats.natural_causes, color: '#2E7D32' },
        { name: 'Human', value: stats.human_causes, color: '#C62828' },
      ]
    : [];

  const statusData: StatsChartData[] = stats
    ? [
        { name: 'Out of Control', value: stats.out_of_control, color: STATUS_COLORS['Out of Control'] },
        { name: 'Being Held', value: stats.being_held, color: STATUS_COLORS['Being Held'] },
        { name: 'Under Control', value: stats.under_control, color: STATUS_COLORS['Under Control'] },
        { name: 'Declared Out', value: stats.declared_out, color: STATUS_COLORS['Declared Out'] },
        { name: 'New', value: stats.new_fires, color: STATUS_COLORS['New'] },
      ]
    : [];

  const responseData: StatsChartData[] = stats
    ? [
        { name: 'Full Response', value: stats.full_response, color: RESPONSE_COLORS['Full Response'] },
        { name: 'Modified Response', value: stats.modified_response, color: RESPONSE_COLORS['Modified Response'] },
        { name: 'Monitor', value: stats.monitor_response, color: RESPONSE_COLORS['Monitor'] },
      ]
    : [];

  const fireComparison: StatsChartData[] = Object.entries(activeYear.regions).map(([name, s]) => ({
    name: canonicalRegionLabel(name),
    value: s.total_fires,
  }));

  const areaComparison: StatsChartData[] = Object.entries(activeYear.regions).map(([name, s]) => ({
    name: canonicalRegionLabel(name),
    value: s.total_area,
  }));

  return (
    <div className="stats-historical-tab">
      <div className="stats-year-tabs">
        {data.map((y, idx) => (
          <button
            key={y.year}
            className={`stats-sub-tab ${activeYearIdx === idx ? 'stats-sub-tab--active' : ''}`}
            onClick={() => setActiveYearIdx(idx)}
          >
            {y.year}
          </button>
        ))}
      </div>
      <div className="stats-sub-tabs">
        {REGIONS.map(region => (
          <button
            key={region}
            className={`stats-sub-tab ${activeRegion === region ? 'stats-sub-tab--active' : ''}`}
            onClick={() => setActiveRegion(region)}
          >
            {region}
          </button>
        ))}
      </div>
      {stats && (
        <>
        <div className="stats-blocks">
          <StatBlock title="Totals" items={[
            { label: 'Fire Frequency', value: stats.total_fires },
            { label: 'Hectares Burned', value: asHectares(stats.total_area) },
          ]} />
          <StatBlock title="Cause" items={toStatItems(causeData)} />
          <StatBlock title="Status" items={toStatItems(statusData)} />
          <StatBlock title="Response" items={toStatItems(responseData)} />
          <StatBlock title="Regional Fire Count" items={toStatItems(fireComparison)} />
          <StatBlock title="Regional Hectares" items={toStatItems(areaComparison, asHectares)} />
        </div>
        <div className="stats-charts-grid">
          <StatsPieChart data={causeData} title="Cause Breakdown" />
          <StatsPieChart data={statusData} title="Status Breakdown" />
          <StatsPieChart data={responseData} title="Response Breakdown" />
          <StatsBarChart data={fireComparison} title="Regional Fire Comparison" color="#1976D2" />
          <StatsBarChart data={areaComparison} title="Regional Area Comparison" color="#F57C00" />
        </div>
        </>
      )}
    </div>
  );
};

export default HistoricalTab;
